import { lazy, Suspense, useEffect } from "react"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import "./i18n/i18n"

import EnvBanner from "./components/EnvBanner"
import { PwaUpdatePrompt } from "./components/PwaUpdatePrompt"
import * as Sentry from "@sentry/react"
import { RouteFallback } from "@/components/RouteFallback";

// Solana wallet adapters (Phantom/Solflare SDKs + web3.js + adapter UI) are the
// heaviest thing that used to ship in the entry chunk — downloaded even on the
// landing and the whole mini-app, neither of which connects a wallet. Lazy-load
// them and mount only around wallet-capable routes (see `needsWallet` below).
const Providers = lazy(() => import("./providers/SolanaWalletProvider"))

// SparkLayout pulls in framer-motion (~50 kB gz) but is used only on secondary
// pages (profile / back-office / legal) — never the landing, ideas or mini-app,
// which render the bare layout. Lazy so the primary surfaces don't pay for it.
const SparkLayout = lazy(() => import("./components/SparkLanding/SparkLayout"))

// Sentry — error monitoring + performance tracing + session replay. Reports to
// the one Spark project (Cloudflare Pages). The DSN is public (it ships in the
// browser bundle anyway), so it's hardcoded — a prod deploy needs no env var.
// Init runs ONLY in built deploys (`import.meta.env.PROD`), never in local dev,
// so localhost errors never pollute the project.
//
// Session Replay masks ALL text + inputs and blocks media: this is a
// custodial-wallet app (balances, addresses, the PIN pad, any private-key
// view), so nothing legible is ever recorded in a replay.
const SENTRY_DSN =
  "https://52c9c93701fd74d03d82f5eec62f5846@o4511786198827008.ingest.us.sentry.io/4511786336780288"
// Sentry environment tag: explicit VITE_SENTRY_ENVIRONMENT wins; otherwise derive
// from the build's env type ("develop" is the staging deploy — see session.ts —
// so it maps to "staging"; a production build maps to "production"). Keeps stage
// and prod as separate environments in Sentry with no extra config.
const rawEnvType = import.meta.env.VITE_ENVIRONMENT_TYPE as string | undefined
const SENTRY_ENVIRONMENT =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
  (rawEnvType === "develop" ? "staging" : rawEnvType) ||
  "production"
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    // Set at build time (e.g. the git SHA) → ties errors to a commit and lets
    // uploaded source maps resolve. Undefined = release-less, still works.
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // Structured logs product (emit with Sentry.logger.*). Console isn't
    // auto-forwarded here — the browser console carries benign noise.
    enableLogs: true,
    // Performance tracing. Propagate the trace to our same-origin `/api`
    // (Cloudflare Pages Functions) so front + back errors share one trace.
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost", /^\/api\//, /^https:\/\/([a-z0-9-]+\.)*justspark\.fun/],
    // Session Replay: 10% of ordinary sessions, 100% of sessions with an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Drop noise that isn't an actionable app error: the stale-chunk deploy
    // reloads (they auto-heal — see main.tsx) and user-rejected wallet actions.
    ignoreErrors: [
      /dynamically imported module/i,
      /Importing a module script failed/i,
      /Failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
      /reading 'default'/i,
      /User rejected/i,
      /Transaction rejected/i,

      // Wallet-extension noise. These are thrown inside the visitor's browser
      // extensions, not by this app, and there is no version of our code that
      // prevents them:
      //   - "disconnected from all chains" is EIP-1193 code 4900, raised by an
      //     extension's own background script (NODE-CLOUDFLARE-PAGES-A / -B).
      //   - "Cannot redefine property: ethereum" is two wallet extensions
      //     racing to own window.ethereum (-C).
      //   - MetaMask connect failures are the user's extension state (-9).
      // Left visible in the browser console; only kept out of Sentry, where
      // they crowd out errors we can act on.
      /disconnected from all chains/i,
      /Cannot redefine property: ethereum/i,
      /Failed to connect to MetaMask/i,
      /ethereum is not defined/i,
    ],
    // Same reasoning, by origin rather than message: anything whose stack
    // frames come from an extension bundle is not our code.
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
  })
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture referral code from URL (?ref=CODE) and store in localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('spark_referral_code', refCode);
      searchParams.delete('ref');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // iPhone PWA fix: iOS Safari uses the *current* URL at "Add to Home
  // Screen" time instead of the manifest's `start_url`. Users who bookmark
  // the install prompt from `/` end up opening the PWA on `/` every launch,
  // which shows the desktop landing page instead of the mini-app.
  //
  // When the app is running standalone (home-screen launch) on an iOS
  // device and we land on `/`, redirect to `/m`. Android/desktop respect
  // the manifest `start_url` so we don't touch them.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.pathname !== '/') return;
    const nav = window.navigator as Navigator & { standalone?: boolean };
    const ua = nav.userAgent || '';
    const isIos =
      /iPhone|iPad|iPod/.test(ua) ||
      (/Macintosh/.test(ua) && nav.maxTouchPoints > 1);
    const isStandalone =
      nav.standalone === true ||
      window.matchMedia?.('(display-mode: standalone)').matches;
    if (isIos && isStandalone) {
      navigate('/mini-app', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Shown while a lazily-loaded route chunk downloads. A bare spinner reads as
  // an instant transition — until the chunk never arrives, and then it reads as
  // a frozen app with no way out. RouteFallback keeps the spinner but gives it
  // a deadline and an escape.
  const routeFallback = <RouteFallback />;

  const isLandingPage = location.pathname === '/';
  // Static press/brand page — own marketing chrome, no wallet adapter.
  const isBrandPage = location.pathname === '/brand';
  const isIdeasPage = location.pathname === '/ideas' || location.pathname.startsWith('/ideas/') ||
    location.pathname === '/teams' || location.pathname === '/explanation' || location.pathname === '/roadmap';
  const isPublicProfile = location.pathname.startsWith('/profile/');

  // Mini-app (/mini-app/*) ships its own `MiniLayout` with a mobile bottom
  // nav — SparkLayout would inject a desktop header + back button on top,
  // which breaks the PWA chrome we want.
  const isMiniApp = location.pathname === '/mini-app' || location.pathname.startsWith('/mini-app/');

  // Warm the hot next-route chunks while the browser is idle so the first
  // navigation doesn't stall on a chunk download (routes are code-split per
  // page). Same import specifiers as main.tsx's lazy() → same chunk URLs, so
  // this only primes the HTTP cache; nothing extra ships in the entry.
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      if (isMiniApp) {
        import("./pages/mini/MiniHackathonDetailPage");
        import("./pages/mini/MiniTradePage");
        import("./pages/mini/MiniMePage");
      } else {
        import("./pages/FundedPage");
        import("./pages/IdeaDetailPage");
        import("./pages/ExplanationPage");
      }
    };
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    // Gate on `load` first. The idle callback carried `timeout: 4000`, which
    // FORCES the work to run at 4s whether or not the browser is idle — on a
    // slow connection the page isn't painted yet at that point, so warming
    // three heavy routes (MiniTradePage alone pulls the 167 kB combinator SDK)
    // stole bandwidth from the content the user is waiting for.
    // After `load` the critical path is done and the prefetch is pure upside.
    let idleId: number | undefined;
    const schedule = () => {
      if (w.requestIdleCallback) idleId = w.requestIdleCallback(warm, { timeout: 3000 });
      else setTimeout(warm, 300);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
    };
  }, [isMiniApp]);

  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/discover') return 'Discover';
    if (path === '/projects') return 'Explore';
    if (path === '/search') return 'Search';
    if (path === '/apply') return 'Apply';
    if (path === '/volume') return 'Volume';
    if (path === '/fees') return 'Fees';
    if (path === '/ideas') return 'Ideas';
    return undefined; // No title for other pages
  };

  // Only wallet-capable routes mount the (lazy) Solana provider; the landing and
  // the whole mini-app skip the adapter download entirely. App's own chrome never
  // uses wallet context, so wrapping just the Outlet covers the pages that do.
  const needsWallet = !isLandingPage && !isMiniApp && !isBrandPage;
  const routedOutlet = needsWallet ? (
    <Suspense fallback={routeFallback}>
      <Providers>
        <Outlet />
      </Providers>
    </Suspense>
  ) : (
    <Suspense fallback={routeFallback}>
      <Outlet />
    </Suspense>
  );

  if (isLandingPage || isBrandPage || isIdeasPage || isPublicProfile || isMiniApp) {
    // Landing page, Ideas page, and Public Profile have their own layout
    return (
      <>
        <EnvBanner />
        <PwaUpdatePrompt />
        {/* <OnboardingModal /> */}
        <ToastContainer theme="dark" />
        {routedOutlet}
      </>
    );
  }

  // All other pages use the Spark layout (lazy — see the import note above).
  return (
    <Suspense fallback={routeFallback}>
      <SparkLayout pageTitle={getPageTitle()} showFooter={false}>
        <EnvBanner />
        <PwaUpdatePrompt />
        {/* <OnboardingModal /> */}
        <ToastContainer theme="dark" />
        {routedOutlet}
      </SparkLayout>
    </Suspense>
  );
}

export default App
